import {DataTestAttrs} from '@benchling/js-test-utils';
import React, {ReactElement} from 'react';

import {assert} from 'Asserts';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import SelectDropdownWithSettings, {
  SelectDropdownWithSettingsProps,
} from 'ui-library/dropdowns/SelectDropdownWithSettings';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {NULL_OPTION_NAME} from '../VisualizationConfigEditorConstants';

export const DEFAULT_NAME_SET_WITHOUT_ALLOW_NULL_ERROR =
  'Allow null must be set to true when nullOptionName is set';

export interface StringValueDropdownSelectorProps<ValueType = string> {
  allowNull: boolean;
  errorMessage?: string | null;
  onChange: (newValue: ValueType | null) => void;
  searchable: boolean;
  selectedValue: ValueType | null;
  values: Array<ValueType>;
  valueToReadable?: (value: ValueType) => string | ReactElement;
  nullOptionName?: string | null;
  rightSideIconProps?: SelectDropdownWithSettingsProps['settingsIconProps'];
  isDisabled?: boolean;
}

export function StringValueDropdownSelector<ValueType extends string = string>({
  values,
  valueToReadable = (value) => value,
  onChange,
  isDisabled = false,
  ...props
}: StringValueDropdownSelectorProps<ValueType>): JSX.Element {
  const optionDatas: Array<OptionData> = values.map((value) => ({
    name: valueToReadable(value),
    value,
    dataTestAttrs: {
      component: 'DropdownSelector',
      element: 'option',
      key: value,
    },
    dataAmpSafe: false,
  }));

  return (
    <DropdownSelector
      {...props}
      optionDatas={optionDatas}
      onChange={(selectedOption: ValueType | null) => {
        onChange(selectedOption);
      }}
      disabled={isDisabled}
    />
  );
}

export interface DropdownSelectorProps<ValueType extends string | number | boolean = string> {
  allowNull: boolean;
  /** @default ButtonSize.SMALL */
  buttonSize?: ButtonSize;
  errorMessage?: string | null;
  onChange: (selectedValue: ValueType | null) => void;
  searchable: boolean;
  selectedValue: ValueType | null;
  optionDatas: Array<OptionData>;
  /**
   * Display text for the `null` option; shows up both in the dropdown menu and when selectedValue == null.
   *
   * `allowNull` must be true if this is provided.
   *
   * @default NULL_OPTION_NAME (only applicable when `allowNull` is true)
   */
  nullOptionName?: string | null;
  settingsIconProps?: SelectDropdownWithSettingsProps['settingsIconProps'];
  disabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function DropdownSelector<ValueType extends string | number | boolean = string>({
  allowNull,
  buttonSize,
  errorMessage = null,
  onChange,
  selectedValue,
  searchable,
  optionDatas: inputOptionDatas,
  nullOptionName = null,
  settingsIconProps,
  disabled,
  dataTestAttrs = {},
}: DropdownSelectorProps<ValueType>): JSX.Element {
  assert(nullOptionName == null || allowNull, DEFAULT_NAME_SET_WITHOUT_ALLOW_NULL_ERROR);

  const optionDatas = allowNull
    ? [{name: nullOptionName ?? NULL_OPTION_NAME, value: null}, ...inputOptionDatas]
    : inputOptionDatas;

  const selectedOption = optionDatas.find((option) => option.value === selectedValue);
  const displayText =
    selectedOption == null ? (selectedValue == null ? NULL_OPTION_NAME : selectedValue) : selectedOption.name;

  const menu = searchable ? (
    <SearchableMenu
      height={MenuHeight.MEDIUM}
      width={MenuWidth.FULL}
      selectedValue={selectedOption?.value}
      onSelect={onChange}
      optionDatas={optionDatas}
    />
  ) : (
    <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
      {optionDatas.map((optionData, i) => (
        <Option
          key={i}
          onSelect={() => {
            onChange(optionData.value);
          }}
          value={optionData.value}
          dataTestAttrs={optionData.dataTestAttrs}
          dataAmpSafe={optionData.dataAmpSafe}
        >
          {optionData.name}
        </Option>
      ))}
    </Menu>
  );

  return settingsIconProps == null ? (
    <SelectDropdown
      menu={menu}
      error={errorMessage}
      buttonSize={buttonSize ?? ButtonSize.SMALL}
      errorStyle={SelectDropdownErrorStyle.TOOLTIP}
      errorTooltipPlacement={FloatingPlacement.LEFT}
      disabled={disabled || errorMessage != null}
      displayName={displayText}
      dataTestAttrs={{
        component: 'DropdownSelector',
        element: 'button',
        ...dataTestAttrs,
      }}
    />
  ) : (
    <SelectDropdownWithSettings
      menu={menu}
      error={errorMessage}
      buttonSize={buttonSize ?? ButtonSize.SMALL}
      errorStyle={SelectDropdownErrorStyle.TOOLTIP}
      errorTooltipPlacement={FloatingPlacement.LEFT}
      disabled={disabled || errorMessage != null}
      displayName={displayText}
      dataTestAttrs={{
        component: 'DropdownSelector',
        element: 'button',
      }}
      settingsIconProps={settingsIconProps}
    />
  );
}
